import React, { Component, Fragment } from "react";
import { toast } from "react-toastify";
import { IFormulaOveridesConfigProps } from "./defs";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Box,
  ThemeProvider,
} from "@mui/material";
import {
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CancelOutlined,
  AddOutlined,
} from "@mui/icons-material";
import withRouter from "../../withRouter";
import { validateRTFormula } from "App/utils/formulaValidator";
import { baseTheme, tableTheme } from "mui-theme";
import { ACTIONS, hasPermission } from "App/common/permissionHandler";
import { Form, Formik } from "formik";
import * as Yup from "yup";

// Validation schema for formula inputs
const formulaValidationSchema = Yup.object().shape({
  label: Yup.string().required("Mandatory field"),
  formula: Yup.string()
    .required("Mandatory field")
    .test("valid-formula", "Invalid formula", validateRTFormula),
});

interface ExtendedFormulaOverideState {
  formulas: { label: string; formula: string; id?: string }[];
  isEdited: boolean;
  defaultLabel: string;
  editingRowId: string | null; // Change from editingRowIndex to editingRowId
  showAddDialog: boolean;
  showDeleteConfirm: boolean;
  rowToDeleteIndex: number | null;
}

class FormulaOverides extends Component<
  IFormulaOveridesConfigProps,
  ExtendedFormulaOverideState
> {
  state: ExtendedFormulaOverideState = {
    formulas: [],
    isEdited: false,
    defaultLabel: "",
    editingRowId: null, // Initialize as null
    showAddDialog: false,
    showDeleteConfirm: false,
    rowToDeleteIndex: null,
  };

  constructor(props: IFormulaOveridesConfigProps) {
    super(props);
    const formulas =
      props.hotel.cm.channelConfig.ows.SeasonnalConfig?.formulas || [];

    // Add unique IDs to each formula for stable identification
    const formulasWithIds = JSON.parse(JSON.stringify(formulas)).map(
      (formula: any, index: number) => ({
        ...formula,
        id: formula.id || `formula-${index}-${Date.now()}`,
      }),
    );

    this.state = {
      ...this.state,
      formulas: formulasWithIds,
      defaultLabel: props.hotel.cm.channelConfig.ows.label || "Default",
    };
  }

  componentDidUpdate(prevProps: IFormulaOveridesConfigProps) {
    if (prevProps.isEdited !== this.props.isEdited) {
      if (this.props.isEdited === true) {
        this.setState({ isEdited: false });
      }
    }
    if (
      prevProps.hotel.cm.channelConfig.ows.SeasonnalConfig?.formulas !==
      this.props.hotel.cm.channelConfig.ows.SeasonnalConfig?.formulas
    ) {
      // Add unique IDs to each formula for stable identification
      const formulas =
        this.props.hotel.cm.channelConfig.ows.SeasonnalConfig?.formulas || [];
      const formulasWithIds = JSON.parse(JSON.stringify(formulas)).map(
        (formula: any, index: number) => ({
          ...formula,
          id: formula.id || `formula-${index}-${Date.now()}`,
        }),
      );

      this.setState({
        formulas: formulasWithIds,
        editingRowId: null,
      });
    }
  }

  notifyUpdate = (updatedFormulas: any[]) => {
    const updatedHotel = {
      ...this.props.hotel,
      cm: {
        ...this.props.hotel.cm,
        channelConfig: {
          ...this.props.hotel.cm.channelConfig,
          ows: {
            ...this.props.hotel.cm.channelConfig.ows,
            SeasonnalConfig: {
              ...this.props.hotel.cm.channelConfig.ows.SeasonnalConfig,
              formulas: [...updatedFormulas],
            },
          },
        },
      },
    };
    this.props.onUpdate(updatedHotel);
  };

  validateUniqueLabel = (
    label: string,
    rowIndexToExclude?: number,
  ): boolean => {
    return !this.state.formulas.some(
      (item, index) =>
        index !== rowIndexToExclude &&
        item.label.toLowerCase() === label.toLowerCase(),
    );
  };

  handleEditRow = (id: string) => {
    this.setState({
      editingRowId: id,
    });
  };

  handleCancelEdit = () => {
    this.setState({
      editingRowId: null,
    });
  };

  handleSaveRow = (
    values: { label: string; formula: string },
    rowId: string,
  ) => {
    // Find the index based on ID
    const index = this.state.formulas.findIndex((f) => f.id === rowId);
    if (index === -1) return;

    // Check if the label is unique (excluding the current row)
    if (!this.validateUniqueLabel(values.label, index)) {
      toast.error("Duplicate label not allowed");
      return;
    }

    const formulas = [...this.state.formulas];
    const updatedFormula = {
      ...formulas[index],
      label: values.label,
      formula: values.formula,
    };

    formulas[index] = updatedFormula;
    this.setState({ formulas, editingRowId: null });
    this.notifyUpdate(formulas);
    this.props.onDirty();
    toast.success("Override updated.");
  };

  handleOpenAddDialog = () => {
    this.setState({
      showAddDialog: true,
    });
  };

  handleCloseAddDialog = () => {
    this.setState({ showAddDialog: false });
  };

  handleAddNewRow = (
    values: { label: string; formula: string },
    { resetForm }: any,
  ) => {
    // Check if the label is unique
    if (!this.validateUniqueLabel(values.label)) {
      toast.error("Duplicate label not allowed");
      return;
    }

    const newFormula = {
      ...values,
      id: `formula-${this.state.formulas.length}-${Date.now()}`,
    };

    const formulas = [...this.state.formulas, newFormula];
    this.setState({ formulas, showAddDialog: false });
    this.notifyUpdate(formulas);
    this.props.onDirty();
    resetForm();
    toast.success("Override added.");
  };

  handleOpenDeleteConfirm = (id: string) => {
    const index = this.state.formulas.findIndex((formula) => formula.id === id);
    if (index !== -1) {
      this.setState({ showDeleteConfirm: true, rowToDeleteIndex: index });
    }
  };

  handleCloseDeleteConfirm = () => {
    this.setState({ showDeleteConfirm: false, rowToDeleteIndex: null });
  };

  handleConfirmDelete = () => {
    if (this.state.rowToDeleteIndex !== null) {
      const formulas = [...this.state.formulas];
      const deletedItem = formulas.splice(this.state.rowToDeleteIndex, 1)[0];
      this.setState({
        formulas,
        showDeleteConfirm: false,
        rowToDeleteIndex: null,
      });
      this.notifyUpdate(formulas);
      this.props.onDirty();
      toast.success(`Override "${deletedItem.label}" deleted.`);
    }
  };

  render() {
    // Sort the data by formula value (but don't modify state)
    const data = [...this.state.formulas];
    if (data.length > 0) {
      data.forEach((item: { formula: string; order?: number }) => {
        item.order = Number(item.formula.replace(/BR/g, "").replace(/\s/g, ""));
      });
      data.sort(
        (
          a: { formula: string; order?: number },
          b: { formula: string; order?: number },
        ) => (b.order ?? 0) - (a.order ?? 0),
      );
    }

    const isAdmin = hasPermission(ACTIONS.EDIT_OWS);

    return (
      <Fragment>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <h6 style={{ margin: 0, alignSelf: "center" }}>
            OWS Formula Overrides
          </h6>
          {isAdmin && (
            <Button
              color="info"
              variant="contained"
              startIcon={<AddOutlined />}
              onClick={this.handleOpenAddDialog}
              id="add-new-ows"
            >
              Add Override
            </Button>
          )}
        </Box>
        <ThemeProvider theme={tableTheme}>
          <TableContainer>
            <Table aria-label="ows formula overrides table">
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell>Formula</TableCell>
                  {isAdmin && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row) => {
                  const isEditing = this.state.editingRowId === row.id;

                  return (
                    <TableRow key={row.id}>
                      {isEditing ? (
                        <Formik
                          initialValues={{
                            label: row.label,
                            formula: row.formula,
                          }}
                          validationSchema={formulaValidationSchema}
                          onSubmit={(values) =>
                            this.handleSaveRow(values, row.id!)
                          }
                        >
                          {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                          }) => (
                            <>
                              <TableCell component="th" scope="row">
                                <TextField
                                  name="label"
                                  value={values.label}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  size="small"
                                  fullWidth
                                  error={touched.label && Boolean(errors.label)}
                                  helperText={touched.label && errors.label}
                                  inputProps={{ maxLength: 30 }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  name="formula"
                                  value={values.formula}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  size="small"
                                  fullWidth
                                  error={
                                    touched.formula && Boolean(errors.formula)
                                  }
                                  helperText={touched.formula && errors.formula}
                                  inputProps={{ maxLength: 20 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Save">
                                  <IconButton
                                    onClick={() => {
                                      // Directly call handleSubmit to validate and submit
                                      if (errors.label || errors.formula) {
                                        toast.error(
                                          "Please fix validation errors",
                                        );
                                        return;
                                      }
                                      this.handleSaveRow(values, row.id!);
                                    }}
                                    color="primary"
                                    id={`save-ows-${row.id}`}
                                  >
                                    <SaveOutlined />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <IconButton
                                    onClick={this.handleCancelEdit}
                                    color="secondary"
                                    id={`cancel-ows-${row.id}`}
                                  >
                                    <CancelOutlined />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </>
                          )}
                        </Formik>
                      ) : (
                        <>
                          <TableCell component="th" scope="row">
                            {row.label}
                          </TableCell>
                          <TableCell>{row.formula}</TableCell>
                          {isAdmin && (
                            <TableCell align="right">
                              <Tooltip title="Edit">
                                <IconButton
                                  onClick={() => this.handleEditRow(row.id!)}
                                  color="primary"
                                  id={`edit-ows-${row.id}`}
                                >
                                  <EditOutlined />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() =>
                                    this.handleOpenDeleteConfirm(row.id!)
                                  }
                                  color="error"
                                  id={`delete-ows-${row.id}`}
                                >
                                  <DeleteOutlined />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          )}
                        </>
                      )}
                    </TableRow>
                  );
                })}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 3 : 2} align="center">
                      No overrides defined.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </ThemeProvider>

        <ThemeProvider theme={baseTheme}>
          <Dialog
            open={this.state.showAddDialog}
            onClose={this.handleCloseAddDialog}
          >
            <DialogTitle>Add New OWS Override</DialogTitle>
            <Formik
              initialValues={{
                label: "",
                formula: "",
              }}
              validationSchema={formulaValidationSchema}
              onSubmit={this.handleAddNewRow}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                resetForm,
              }) => (
                <>
                  <DialogContent>
                    <TextField
                      autoFocus
                      margin="dense"
                      id="new-label"
                      name="label"
                      label="Label"
                      type="text"
                      fullWidth
                      variant="standard"
                      value={values.label}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.label && Boolean(errors.label)}
                      helperText={touched.label && errors.label}
                      inputProps={{ maxLength: 30 }}
                    />
                    <TextField
                      margin="dense"
                      id="new-formula"
                      name="formula"
                      label="Formula"
                      type="text"
                      fullWidth
                      variant="standard"
                      value={values.formula}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.formula && Boolean(errors.formula)}
                      helperText={touched.formula && errors.formula}
                      inputProps={{ maxLength: 20 }}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={this.handleCloseAddDialog}
                      color="secondary"
                      variant="contained"
                      type="button"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={() => {
                        // Direct validation and submission
                        if (errors.label || errors.formula) {
                          toast.error("Please fix validation errors");
                          return;
                        }
                        this.handleAddNewRow(values, { resetForm });
                      }}
                      id="add-ows-confirm"
                      color="primary"
                      variant="contained"
                      disabled={isSubmitting}
                    >
                      Add
                    </Button>
                  </DialogActions>
                </>
              )}
            </Formik>
          </Dialog>
        </ThemeProvider>

        <ThemeProvider theme={baseTheme}>
          <Dialog
            open={this.state.showDeleteConfirm}
            onClose={this.handleCloseDeleteConfirm}
          >
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              Are you sure you want to delete the override &quot;
              {this.state.rowToDeleteIndex !== null
                ? this.state.formulas[this.state.rowToDeleteIndex]?.label
                : ""}
              &quot;? This cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button
                onClick={this.handleCloseDeleteConfirm}
                color="secondary"
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                onClick={this.handleConfirmDelete}
                color="primary"
                id="delete-ows-confirm"
                variant="contained"
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      </Fragment>
    );
  }
}
export default withRouter(FormulaOverides);
